package ai.wagemix.web.screens.mixes

import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.mixes.WMDefaultSavedMixesPresenter
import ai.wagemix.logic.mixes.WMSavedMixCard
import ai.wagemix.logic.mixes.WMSavedMixesFilter
import ai.wagemix.logic.mixes.WMSavedMixesPresenter
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Action
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Event
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Prompt
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder

interface WMSavedMixesWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    val cardsState: State<List<WMSavedMixCard>>
    val hasMixesState: State<Boolean>
    val filterState: State<WMSavedMixesFilter>
    val queryState: State<String>
    /** The subscription ended: the page shows the renewal state instead of the mixes (paid-feature plan W7). */
    val lapsedState: State<Boolean>
}

abstract class WMBaseSavedMixesWebViewModel<
    P: Prompt,
    E: Event<P>,
    A: Action,
    PR: WMSavedMixesPresenter<P, E, A>
>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit,
    /** The page opens the share dialog for this mix. */
    protected val onShareRequest: (mixId: String) -> Unit
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMSavedMixesWebViewStateHolder<P> {

    override val cardsState: State<List<WMSavedMixCard>> get() = _cardsState
    protected val _cardsState = mutableStateOf<List<WMSavedMixCard>>(emptyList())

    override val hasMixesState: State<Boolean> get() = _hasMixesState
    protected val _hasMixesState = mutableStateOf(false)

    override val filterState: State<WMSavedMixesFilter> get() = _filterState
    protected val _filterState = mutableStateOf(WMSavedMixesFilter.All)

    override val queryState: State<String> get() = _queryState
    protected val _queryState = mutableStateOf("")

    override val lapsedState: State<Boolean> get() = _lapsedState
    protected val _lapsedState = mutableStateOf(false)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            cardsUpdate?.let { _cardsState.value = it }
            hasMixesUpdate?.let { _hasMixesState.value = it }
            filterUpdate?.let { _filterState.value = it }
            queryUpdate?.let { _queryState.value = it }
            lapsedUpdate?.let { _lapsedState.value = it }
            shareRequestUpdate?.let { onShareRequest(it) }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userChangedListParameters(filter: WMSavedMixesFilter, query: String) = presenter.userChangedListParameters(filter, query)
    fun userRequestedOpen(mixId: String) = presenter.userRequestedOpen(mixId)
    fun userRequestedShare(mixId: String) = presenter.userRequestedShare(mixId)
    fun userRequestedRename(mixId: String) = presenter.userRequestedRename(mixId)
    fun userConfirmedRename(mixId: String, title: String) = presenter.userConfirmedRename(mixId, title)
    fun userRequestedDuplicate(mixId: String) = presenter.userRequestedDuplicate(mixId)
    fun userRequestedDelete(mixId: String) = presenter.userRequestedDelete(mixId)
}

typealias WMDefaultSavedMixesWebViewModel = WMBaseSavedMixesWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultSavedMixesPresenter>

fun createDefaultSavedMixesWebViewModel(
    presenter: WMDefaultSavedMixesPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit,
    onShareRequest: (String) -> Unit
): WMDefaultSavedMixesWebViewModel = object: WMDefaultSavedMixesWebViewModel(presenter, externalNavigation, onShareRequest) {
    override val defaultPrompt: Prompt.Base
        get() = Prompt.baseFactory().none()
}
